<!-- BoQ list aligned with Project Management dashboard -->
<div class="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">

  <!-- Header -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          Bill of Quantities (BoQ)
        </h1>
        <p class="text-gray-600">
          Manage and track project BoQs for tendering and cost estimation
        </p>
      </div>

      <div class="flex flex-wrap gap-4">
        <div class="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-600">Total BoQs</p>
              <p class="text-lg font-semibold text-gray-900">{{ totalItems }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Filters & actions -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
    <div class="flex flex-col gap-4">
      <div class="flex flex-col xl:flex-row xl:items-center gap-4">
        <!-- Search -->
        <div class="flex-1 min-w-0">
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              [(ngModel)]="searchKeyword"
              (keyup.enter)="applyFilter()"
              placeholder="Search by code, name, or description..."
              class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
            />
          </div>
        </div>

        <!-- Filters -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 xl:flex xl:flex-nowrap xl:gap-3">
          <select
            [(ngModel)]="selectedStatus"
            (change)="applyFilter()"
            class="w-full xl:w-48 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
          >
            <option *ngFor="let status of statusOptions" [value]="status.value">{{ status.label }}</option>
          </select>
          <select
            [(ngModel)]="selectedEstimationStatus"
            (change)="applyFilter()"
            class="w-full xl:w-48 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
          >
            <option *ngFor="let opt of estimationStatusOptions" [value]="opt.value">{{ opt.label }}</option>
          </select>
          <select
            [(ngModel)]="selectedProject"
            (change)="applyFilter()"
            class="w-full xl:w-56 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
          >
            <option value="">All Projects</option>
            <option *ngFor="let project of projects" [value]="project.id">{{ project.project_name }}</option>
          </select>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1 border-t border-gray-100 xl:border-t-0 xl:pt-0">
        <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            [(ngModel)]="assignedToMe"
            (change)="applyFilter()"
            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Assigned to me
        </label>

        <div class="flex flex-wrap items-center gap-2 sm:ml-auto">
          <button
            type="button"
            (click)="clearFilters()"
            class="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear Filters
          </button>
          <button
            type="button"
            (click)="loadBoQs()"
            class="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button
            type="button"
            (click)="createBoQ()"
            class="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create BoQ
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-lg border border-gray-200">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
    <p class="text-gray-600">Loading BoQs...</p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading" class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-lg font-semibold text-gray-900">BoQs overview</h2>
      <p class="text-sm text-gray-600 mt-1">Browse, estimate, and manage BoQs tied to projects</p>
    </div>

    <div class="overflow-x-auto">
      <table mat-table [dataSource]="dataSource" matSort class="w-full">
        <ng-container matColumnDef="boq_code">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">BoQ Code</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <div class="font-medium text-blue-600">{{ boq.boq_code }}</div>
            <div class="text-xs text-gray-500">v{{ boq.version }}</div>
          </td>
        </ng-container>

        <!-- <ng-container matColumnDef="boq_name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">BoQ Name</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4">
            <div class="font-medium text-gray-900">{{ boq.boq_name }}</div>
            <div class="text-sm text-gray-500" *ngIf="boq.description">
              {{ boq.description | slice:0:50 }}{{ boq.description.length > 50 ? '...' : '' }}
            </div>
          </td>
        </ng-container> -->

        <ng-container matColumnDef="project_name">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Project</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <span class="text-sm text-gray-700">{{ boq.project_name || 'N/A' }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="assigned_to_name">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Assigned to (PM)</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <span class="text-sm text-gray-700">{{ boq.assigned_to_name || '—' }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="estimation_status">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Estimation</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <span [class]="'inline-flex px-2.5 py-1 rounded-full text-xs font-medium ' + getEstimationStatusClass(boq.estimation_status)">
              {{ (boq.estimation_status || 'draft') | titlecase }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Status</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <span [class]="'inline-flex px-2.5 py-1 rounded-full text-xs font-medium ' + getStatusClass(boq.status)">
              {{ boq.status | titlecase }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="total_amount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Total Amount</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <span class="font-semibold text-gray-900">{{ formatCurrency(boq.total_amount, boq.currency) }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="item_count">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Items</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <span class="text-sm text-gray-600">{{ boq.item_count || 0 }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="created_at">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Created At</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ boq.created_at | date:'short' }}</td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
          <td mat-cell *matCellDef="let boq" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                (click)="openDeviation(boq)"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-amber-600 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors duration-200"
                matTooltip="Planned vs actual"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </button>
              <button mat-icon-button matTooltip="View" (click)="viewBoQ(boq)" class="text-blue-600 hover:bg-blue-50">
                <mat-icon>visibility</mat-icon>
              </button>
              <button mat-icon-button matTooltip="AI BoQ summary" (click)="openAiSummary(boq)" class="text-sky-600 hover:bg-sky-50">
                <mat-icon>auto_awesome</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Invited bidders" (click)="viewBids(boq)" class="text-amber-600 hover:bg-amber-50">
                <mat-icon>groups</mat-icon>
              </button>
              <button mat-icon-button
                [matTooltip]="canSendForBidding(boq) ? 'Send for bidding / invite more' : 'Approve estimation before publishing'"
                (click)="sendForBidding(boq)"
                [disabled]="!canSendForBidding(boq)"
                class="text-indigo-600 hover:bg-indigo-50">
                <mat-icon>gavel</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Copy Bidding Link" (click)="copyBiddingLink(boq)" [disabled]="boq.copyingLink" class="text-purple-600 hover:bg-purple-50">
                <mat-icon *ngIf="!boq.copyingLink">link</mat-icon>
                <mat-spinner *ngIf="boq.copyingLink" diameter="20"></mat-spinner>
              </button>
              <button mat-icon-button matTooltip="Edit" (click)="editBoQ(boq)" class="text-green-600 hover:bg-green-50" [disabled]="boq.status === 'approved'">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Delete" (click)="deleteBoQ(boq)" class="text-red-600 hover:bg-red-50">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-gray-50"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="hover:bg-gray-50 transition-colors duration-150"></tr>
      </table>
    </div>

    <div *ngIf="dataSource.data.length === 0" class="text-center py-12 px-4">
      <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No BoQs found</h3>
      <p class="text-gray-500 mb-6">Create your first BoQ to get started.</p>
      <button
        type="button"
        (click)="createBoQ()"
        class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Create BoQ
      </button>
    </div>

    <div *ngIf="totalItems > 0" class="px-6 py-4 border-t border-gray-200 bg-gray-50">
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="text-sm text-gray-600">
          Showing {{ pageIndex * pageSize + 1 }} to
          {{ (pageIndex + 1) * pageSize > totalItems ? totalItems : (pageIndex + 1) * pageSize }}
          of {{ totalItems }} BoQs
        </div>
        <mat-paginator
          [length]="totalItems"
          [pageSize]="pageSize"
          [pageIndex]="pageIndex"
          [pageSizeOptions]="pageSizeOptions"
          [showFirstLastButtons]="true"
          (page)="onPageChange($event)"
          class="bg-transparent"
        ></mat-paginator>
      </div>
    </div>
  </div>
</div>
